<template>
  <div class="products-page brand-list-page" dir="rtl">
    <div class="products-hero">
      <div>
        <p class="hero-kicker mb-1">مدیریت محصولات</p>
        <h1 class="mb-0">برندها</h1>
      </div>
      <div class="hero-side">
        <div class="hero-count" v-if="pagination && pagination.total != null">
          <strong>{{ formatNumber(pagination.total) }}</strong>
          <span>برند</span>
        </div>
        <div class="hero-badge">
          <b-icon icon="award"></b-icon>
        </div>
      </div>
    </div>

    <div class="products-toolbar">
      <div class="products-toolbar-actions">
        <router-link class="products-tool-btn" :to="{ name: 'CreateBrand' }">
          <b-icon icon="plus-lg"></b-icon>
          <span>افزودن برند</span>
        </router-link>
        <router-link class="products-tool-btn" :to="{ name: 'TrashBrand' }">
          <b-icon icon="trash"></b-icon>
          <span>سطل زباله</span>
        </router-link>
        <LanguageSelectBox class="products-lang" @changeLanguage="onLanguageChange" module-name="brand"/>
      </div>

      <div class="products-filter-bar">
        <div class="products-filter-row brand-filter-row">
          <label class="products-filter-field brand-search-field">
            <span><b-icon icon="search"></b-icon> جستجو</span>
            <input class="products-filter-input" v-model="searchWords" placeholder="نام برند..."
                   @keypress.enter.prevent="triggerSearch">
          </label>
          <button type="button" class="products-more-btn" @click="triggerSearch">
            <b-icon icon="search"></b-icon>
            <span>جستجو</span>
          </button>
          <button v-if="activeSearch" type="button" class="products-more-btn" @click="clearSearch">
            <b-icon icon="x-lg"></b-icon>
            <span>پاک کردن</span>
          </button>
        </div>
      </div>
    </div>

    <div class="products-list-panel">
      <div v-if="listLoading" class="products-loading">
        <div class="spinner-border" role="status"></div>
        <p class="mb-0">در حال بارگذاری...</p>
      </div>

      <div v-else-if="!safeBrands.length" class="products-empty">
        <i class="bi bi-award"></i>
        <p class="mb-0">{{ activeSearch ? 'برندی با این عنوان پیدا نشد.' : 'هنوز برندی ثبت نشده است.' }}</p>
        <router-link class="products-empty-btn" :to="{ name: 'CreateBrand' }">افزودن برند</router-link>
      </div>

      <template v-else>
        <div class="forms-grid brand-grid">
          <article v-for="brand in safeBrands" :key="'card-' + brand.id" class="form-card brand-card">
            <label class="brand-card__media brand-thumb-upload" :class="{ 'is-uploading': uploadingId === brand.id }"
                   v-b-tooltip.hover title="تغییر تصویر">
              <input type="file" accept="image/*" class="d-none" :disabled="uploadingId === brand.id"
                     @change="uploadImage(brand, $event)">
              <img v-if="brand.image" :src="brand.image" alt="">
              <span v-else class="brand-card__placeholder"><b-icon icon="image"/></span>
              <span class="brand-thumb-upload__overlay">
                <b-spinner v-if="uploadingId === brand.id" small/>
                <template v-else><b-icon icon="camera"/> تغییر تصویر</template>
              </span>
            </label>

            <div class="form-card-top">
              <span class="form-id-chip">#{{ brand.id }}</span>
            </div>

            <router-link class="form-card-name" :to="{ name: 'EditBrand', params: { slug: brand.slug } }">
              {{ brand.name }}
            </router-link>

            <div class="form-card-meta">
              <span dir="ltr"><b-icon icon="link-45deg"></b-icon> {{ brand.slug }}</span>
            </div>

            <div class="form-card-actions">
              <router-link class="form-action is-main" :to="{ name: 'EditBrand', params: { slug: brand.slug } }">
                <b-icon icon="pen"></b-icon>
                <span>ویرایش</span>
              </router-link>
              <button type="button" class="form-action is-danger" title="حذف" @click="deleteBrand(brand)">
                <b-icon icon="trash"></b-icon>
              </button>
            </div>
          </article>
        </div>

        <div class="products-pager">
          <m-pagination-400 class="text-center" :meta="pagination" :on-change-page="changePage"/>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import LanguageSelectBox from "../../components/dashboard/Language/LanguageSelectBox";
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter";
import {getAxiosInstance} from "@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";
import "../../components/dashboard/product/products-ui.css";
import "../../components/dashboard/Form/forms-ui.css";

export default {
  name: "Brand",
  components: {LanguageSelectBox},
  data() {
    return {
      listLoading: true,
      awaitingData: false,
      loadingTimer: null,
      searchWords: '',
      activeSearch: '',
      uploadingId: null,
    }
  },
  computed: {
    ...mapGetters({
      brands: 'Brand/getBrands',
      pagination: 'Brand/getPagination',
    }),
    safeBrands() {
      if (Array.isArray(this.brands)) return this.brands
      // the store sometimes keeps brands as an object after create/update
      return this.brands && typeof this.brands === 'object' ? Object.values(this.brands).filter(item => item && item.id) : []
    }
  },
  watch: {
    brands() {
      if (this.awaitingData) this.stopLoading()
    }
  },
  methods: {
    formatNumber,
    ...mapActions({
      removeBrand: 'Brand/removeBrand'
    }),
    startLoading() {
      this.listLoading = true
      this.awaitingData = true
      clearTimeout(this.loadingTimer)
      this.loadingTimer = setTimeout(this.stopLoading, 20000)
    },
    stopLoading() {
      this.awaitingData = false
      this.listLoading = false
      clearTimeout(this.loadingTimer)
      this.loadingTimer = null
    },
    load(page) {
      this.startLoading()
      if (this.activeSearch) {
        this.$store.dispatch('Brand/searchBrand', {form: {words: this.activeSearch}, page: page})
      } else {
        const req = this.$store.dispatch('Brand/getBrands', page)
        if (req && typeof req.catch === 'function') req.catch(() => this.stopLoading())
      }
    },
    changePage(page) {
      this.load(page)
      const currentRoute = this.$route
      this.$router.push({name: currentRoute.name, query: {...currentRoute.query, page: page}})
    },
    getItems() {
      let page = 1;
      if (this.$route.query.page != undefined) {
        page = this.$route.query.page
      }
      this.load(page)
    },
    onLanguageChange() {
      this.getItems()
    },
    triggerSearch() {
      this.activeSearch = (this.searchWords || '').trim()
      this.load(1)
    },
    clearSearch() {
      this.searchWords = ''
      this.activeSearch = ''
      this.load(1)
    },
    deleteBrand(brand) {
      if (confirm('آیا از حذف این برند اطمینان دارید؟')) {
        this.removeBrand(brand);
      }
    },
    async uploadImage(brand, event) {
      const input = event.target
      const file = input.files && input.files[0]
      if (!file || this.uploadingId !== null) return
      // same payload as Brand/updateBrand (brandDataShape + image + PUT)
      const formData = new FormData()
      formData.append('name', brand.name)
      formData.append('slug', brand.slug)
      formData.append('language_id', brand.language_id)
      if (brand.description != null && String(brand.description).trim() !== '') formData.append('description', brand.description)
      formData.append('image', file)
      formData.append('_method', 'PUT')
      this.uploadingId = brand.id
      try {
        const res = await getAxiosInstance().post(`/brand/${brand.id}`, formData)
        const image = res && res.data && res.data.data && res.data.data.image
        this.$set(brand, 'image', image ? `${image}${image.indexOf('?') > -1 ? '&' : '?'}t=${Date.now()}` : URL.createObjectURL(file))
        toast('با موفقیت انجام شد', 'تصویر برند به‌روزرسانی شد', 'success', 'top-left')
      } catch (e) {
        console.error(e)
        toast('خطا', 'بارگذاری تصویر انجام نشد', 'danger', 'top-left')
      } finally {
        this.uploadingId = null
        input.value = ''
      }
    },
  },
  mounted() {
    this.getItems()
  },
  beforeDestroy() {
    clearTimeout(this.loadingTimer)
  }
}
</script>

<style scoped>
.products-page {
  padding: 8px 4px 24px;
}

.products-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1e1e1e 0%, #3a3a3a 55%, #eb3d60 140%);
  color: #fff;
  border-radius: 22px;
  padding: 24px 28px;
  margin-bottom: 22px;
}

.hero-kicker {
  opacity: 0.8;
  font-size: 13px;
}

.products-hero h1 {
  font-size: 28px;
  font-weight: 800;
}

.hero-side {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.hero-count {
  text-align: left;
}

.hero-count strong {
  display: block;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
}

.hero-count span {
  font-size: 12px;
  opacity: 0.8;
}

.hero-badge {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.brand-list-page .brand-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}

.brand-list-page .products-more-btn {
  height: 34px;
  margin-top: 0;
}

.brand-search-field {
  flex: 1 1 280px;
}

.brand-card {
  overflow: hidden;
}

.brand-card__media {
  position: relative;
  display: block;
  margin: -1px -1px 10px;
  aspect-ratio: 16 / 10;
  border-radius: 14px;
  overflow: hidden;
  cursor: pointer;
  background: #f4f4f5;
}

.brand-card__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.brand-card__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #b0b0b8;
  font-size: 2rem;
}

.brand-thumb-upload__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: rgba(30, 30, 30, 0.45);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.brand-thumb-upload:hover .brand-thumb-upload__overlay,
.brand-thumb-upload.is-uploading .brand-thumb-upload__overlay {
  opacity: 1;
}

@media (max-width: 767px) {
  .products-page {
    padding: 4px 0 16px;
  }

  .products-hero {
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 16px;
  }

  .hero-badge {
    display: none;
  }

  .products-hero h1 {
    font-size: 22px;
  }

  .hero-count strong {
    font-size: 20px;
  }
}
</style>
